import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';
import _ from 'lodash';

export default function OpportunityStageDrawer({ opportunityStage, onSaved, onClose, onDeleted }) {
  const title = `${opportunityStage?.id ? 'Edit' : 'New'} Opportunity Stage`;
  useDocumentTitle(title);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const [hasDependencies, setHasDependencies] = useState(true);
  const salesforceReadOnly = !!opportunityStage?.salesforceId;
  const hubspotReadOnly = !!opportunityStage?.hubspotId;

  useEffect(() => {
    if (!opportunityStage?.id) {
      setHasDependencies(false);
      return;
    }

    (async function fetchData() {
      const dependencies = await api.www
        .workspaces(workspace.id)
        .opportunityStages(opportunityStage.id)
        .hasDependencies();
      setHasDependencies(dependencies.data);
    })();
  }, [api, opportunityStage, workspace]);

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  const initialValues = mergeValues(
    {
      name: '',
      probability: '',
      statusId: '',
      description: '',
      opportunityPipeline: null,
    },
    opportunityStage,
  );

  return (
    <>
      <Drawer
        isOpen
        title={title}
        byline="Custom Data"
        onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
        onClose={handleClose}>
        {(closeDrawer) => {
          const handleCloseClick = () => dirtyCheck(() => closeDrawer());

          async function handleSubmit(values, { resetForm }) {
            try {
              form.submit(formRef.current.status.action);

              const body = emptyStringToNull({
                ..._.omit(values, 'opportunityPipeline'),
                opportunityPipelineId: values.opportunityPipeline?.id ?? null,
              });

              const { data } = await api.www
                .workspaces(workspace.id)
                .opportunityStages(opportunityStage ? opportunityStage.id : undefined)
                .upsert(body);

              await onSaved(data);
              form.save(formRef.current.status.action);

              switch (formRef.current.status.action) {
                case 'new':
                  resetForm();
                  toast.success('Opportunity Stage successfully saved.');
                  break;
                case 'close':
                  closeDrawer();
                  break;
              }
              form.done();
            } catch ({ message }) {
              form.error({ message });
            }
          }

          async function handleDelete() {
            const confirm = await confirmation.prompt((resolve) => (
              <DeleteConfirmation resolve={resolve}>
                Are you sure you want to delete this Opportunity Stage?
              </DeleteConfirmation>
            ));
            if (!confirm) return;
            try {
              await api.www.workspaces(workspace.id).opportunityStages(opportunityStage.id).delete();
              await onDeleted();
              closeDrawer();
            } catch ({ message }) {
              toast.error(message);
            }
          }

          return (
            <Formik
              innerRef={formRef}
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={Yup.object().shape({
                name: Yup.string().label('Name').max(255).required(),
                probability: Yup.number().label('Probability').min(0).max(100).required(),
                statusId: Yup.string().label('Category').required(),
                description: Yup.string().label('Description').max(5000).nullable(),
                opportunityPipeline: Yup.object().label('Pipeline').nullable().required(),
              })}>
              {(formik) => {
                const submit = async (action) => {
                  formik.setStatus({ action });
                  formik.submitForm();
                };

                return (
                  <Form>
                    <Form.Section title="General">
                      <Form.Control>
                        <Field.Text
                          autoFocus
                          name="name"
                          placeholder="Name"
                          maxLength={255}
                          disabled={salesforceReadOnly || hubspotReadOnly}
                        />
                      </Form.Control>

                      <Form.Control>
                        <Field.Number
                          name="probability"
                          placeholder="Probability"
                          min={0}
                          max={100}
                          precision={2}
                          prefix="%"
                          style={{ textAlign: 'left' }}
                          disabled={salesforceReadOnly || hubspotReadOnly}
                        />
                      </Form.Control>

                      <Form.Control>
                        <Field.SingleSelect
                          name="statusId"
                          placeholder="Category"
                          disabled={salesforceReadOnly || hubspotReadOnly}>
                          <option value="open">Open</option>
                          <option value="won">Won</option>
                          <option value="lost">Lost</option>
                        </Field.SingleSelect>
                      </Form.Control>

                      <Form.Control>
                        <Field.OpportunityPipelineSelect
                          name="opportunityPipeline"
                          placeholder="Pipeline"
                          disabled={hasDependencies || salesforceReadOnly || hubspotReadOnly}
                        />
                      </Form.Control>

                      <Form.Control>
                        <Field.TextArea
                          name="description"
                          placeholder="Description"
                          maxLength={5000}
                          disabled={salesforceReadOnly || hubspotReadOnly}
                        />
                      </Form.Control>
                    </Form.Section>

                    {status && <FormMessage.Error>{message}</FormMessage.Error>}

                    <Drawer.Actions>
                      {opportunityStage.id && !hasDependencies && !(hubspotReadOnly || salesforceReadOnly) && (
                        <DeleteButton onClick={handleDelete}>Delete</DeleteButton>
                      )}

                      <Buttons align="right">
                        <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                        {!opportunityStage?.id && (
                          <ActionButton
                            isLoading={isSubmitting === 'new'}
                            ok={saved === 'new'}
                            type="submit"
                            onClick={() => submit('new')}>
                            Save &amp; New
                          </ActionButton>
                        )}

                        <ActionButton
                          isLoading={isSubmitting === 'close'}
                          ok={saved === 'close'}
                          type="submit"
                          onClick={() => submit('close')}>
                          Save &amp; Close
                        </ActionButton>
                      </Buttons>
                    </Drawer.Actions>
                  </Form>
                );
              }}
            </Formik>
          );
        }}
      </Drawer>
    </>
  );
}
